<template>
    <div class="deals-search-page">
        <div class="page-header">
            <h1>Deals (Pair-Model)</h1>
            <p class="pair-tagline">
                Ranked by the LambdaRank pairwise model trained on your A/B picks.
                Same filters and sort options as <a href="/deals">/deals</a>; just a different audience-score column.
            </p>
            <input
                v-model="filters.q"
                type="text"
                class="search-box"
                placeholder="Search deals — strollers, diapers, formula…"
            />
        </div>

        <div class="results-bar">
            <span class="result-count" v-if="!loading">
                Showing <strong>{{ deals.length }}</strong> of <strong>{{ total }}</strong> deals
            </span>
            <span class="result-count" v-else>Loading…</span>

            <button class="mobile-filter-btn" @click="drawerOpen = true">
                Filters
                <span v-if="hasActiveFilters" class="filter-dot">●</span>
            </button>

            <label class="sort-label">
                Sort:
                <select v-model="filters.sort" class="sort-select">
                    <option value="best">Best deals (pair model)</option>
                    <option value="newest">Newest</option>
                    <option value="discount">Biggest % off</option>
                    <option value="price">Lowest price</option>
                    <option value="grade">Highest grade</option>
                </select>
            </label>
        </div>

        <div v-if="error" class="error-msg">{{ error }}</div>

        <div class="search-layout">
            <FilterSidebar
                class="desktop-sidebar"
                :filters="filters"
                :facets="facets"
                :hasActive="hasActiveFilters"
                @toggle="onToggle"
                @updateFilter="onUpdateFilter"
                @setCategory="setCategory"
                @reset="resetFilters"
            />

            <div class="results-pane">
                <div v-if="loading && deals.length === 0" class="deals-grid">
                    <div v-for="i in 8" :key="i" class="skeleton-card">
                        <div class="skeleton-img"></div>
                        <div class="skeleton-line"></div>
                        <div class="skeleton-line short"></div>
                        <div class="skeleton-price"></div>
                    </div>
                </div>

                <div v-else-if="!loading && deals.length === 0 && !error" class="empty-state">
                    <p>No matches.</p>
                    <button v-if="hasActiveFilters" @click="resetFilters" class="reset-link">Reset filters</button>
                </div>

                <div class="deals-grid" v-else>
                    <DealCard
                        v-for="deal in deals"
                        :key="deal.dealId"
                        :deal="deal"
                        @open="onDealClick"
                    />
                </div>

                <div class="load-more" v-if="!loading && deals.length < total">
                    <button @click="loadMore" class="load-more-btn">Load more</button>
                </div>
            </div>
        </div>

        <div v-if="drawerOpen" class="drawer-overlay" @click.self="drawerOpen = false">
            <div class="drawer-panel">
                <div class="drawer-header">
                    <span>Filters</span>
                    <button class="drawer-close" @click="drawerOpen = false">×</button>
                </div>
                <FilterSidebar
                    class="drawer-sidebar"
                    :filters="filters"
                    :facets="facets"
                    :hasActive="hasActiveFilters"
                    @toggle="onToggle"
                    @updateFilter="onUpdateFilter"
                    @reset="resetFilters"
                />
            </div>
        </div>

        <MiniDealModal
            v-if="$globalStore.selectedDeal"
            :dealDetails="$globalStore.selectedDeal"
            :showDealPrice="true"
        />
    </div>
</template>

<script setup lang="ts">
// /deals-pairs -- experimental pair-model variant of /deals. Same UI,
// same filters; the only difference is useDealsSearch('pair') which
// makes the API rank by NicheScores.PairAudienceScore instead of the
// label-blend AudienceScore. Side-by-side comparison with /deals lets
// us see which model produces a better-feeling top ordering before
// committing to one.
import { ref } from 'vue'
import { useDealsSearch, type SearchFilters } from '@/composables/useDealsSearch'
import DealCard, { type SearchDeal } from '@/components/DealsSearch/DealCard.vue'
import FilterSidebar from '@/components/DealsSearch/FilterSidebar.vue'
import MiniDealModal from '@/components/MiniDealModal.vue'
import { DealDetails } from '@/classes/DealDetails'
import { globalStore } from '@/stores/globalStore'

const $globalStore = globalStore()

const {
    filters, deals, total, facets, loading, error,
    hasActiveFilters, runSearch, loadMore, toggleMulti, setCategory, resetFilters,
} = useDealsSearch('pair')

const drawerOpen = ref(false)

const onToggle = (dim: keyof SearchFilters, value: string | number) => {
    toggleMulti(dim as any, value as any)
}
const onUpdateFilter = (key: keyof SearchFilters, value: any) => {
    ;(filters as any)[key] = value
}

const onDealClick = (deal: SearchDeal) => {
    const details = new DealDetails({
        dealId: deal.dealId,
        title: deal.title || '',
        imgUrl: deal.imgUrl || '',
        retailPrice: deal.retailPrice ?? undefined,
        dealPrice: deal.dealPrice ?? undefined,
        dealUrl: deal.dealUrl || '',
        promoCode: deal.promoCode || '',
        percentOff: deal.percentOff,
    })
    $globalStore.setSelectedDeal(details, true)
}

runSearch()
</script>

<style scoped>
.deals-search-page { max-width: 1400px; margin: 0 auto; padding: 20px; }
.page-header { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
.page-header h1 { margin: 0; font-size: 24px; color: #222; }
.pair-tagline { margin: 0; font-size: 13px; color: #607d8b; }
.pair-tagline a { color: #1565c0; text-decoration: none; font-weight: 600; }
.pair-tagline a:hover { text-decoration: underline; }

.search-box { width: 100%; padding: 10px 14px; border: 2px solid #cfd8dc; border-radius: 8px; font-size: 15px; }
.search-box:focus { outline: none; border-color: #1565c0; box-shadow: 0 0 0 3px rgba(21,101,192,0.15); }

.results-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 10px; }
.result-count { color: #555; font-size: 14px; }
.sort-label { font-size: 13px; color: #555; display: flex; align-items: center; gap: 6px; }
.sort-select { padding: 5px 10px; border: 1px solid #cfd8dc; border-radius: 4px; font-size: 13px; background: #fff; }

.mobile-filter-btn { display: none; background: #fff; border: 1px solid #cfd8dc; padding: 6px 14px; border-radius: 4px; font-size: 13px; font-weight: 600; cursor: pointer; align-items: center; gap: 6px; }
.filter-dot { color: #1565c0; font-size: 10px; }

.error-msg { color: #c62828; background: #ffebee; padding: 10px 14px; border-radius: 6px; margin-bottom: 12px; }

.search-layout { display: flex; gap: 16px; align-items: flex-start; }
.results-pane { flex: 1; min-width: 0; }

.empty-state { text-align: center; padding: 60px 20px; color: #888; font-size: 15px; }
.empty-state p { margin: 0 0 12px 0; }
.reset-link { background: #1565c0; color: #fff; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 13px; }

.deals-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; }

.skeleton-card { border: 1px solid #e0e0e0; border-radius: 8px; background: #fff; padding: 0 0 12px 0; overflow: hidden; }
.skeleton-img { aspect-ratio: 1 / 1; background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%); background-size: 200% 100%; animation: shimmer 1.4s infinite linear; margin-bottom: 10px; }
.skeleton-line, .skeleton-price { height: 12px; margin: 6px 12px; border-radius: 3px; background: linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%); background-size: 200% 100%; animation: shimmer 1.4s infinite linear; }
.skeleton-line.short { width: 60%; }
.skeleton-price { height: 18px; width: 40%; margin-top: 10px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.load-more { text-align: center; margin-top: 24px; }
.load-more-btn { padding: 10px 24px; background: #1565c0; color: #fff; border: none; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; }
.load-more-btn:hover { background: #0d47a1; }

.drawer-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; }
.drawer-panel { position: absolute; top: 0; left: 0; bottom: 0; width: 85%; max-width: 320px; background: #fff; overflow-y: auto; animation: slideIn 0.2s ease-out; }
@keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
.drawer-header { display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; background: #37474f; color: #fff; font-weight: 600; }
.drawer-close { background: none; border: none; color: #fff; font-size: 28px; cursor: pointer; line-height: 1; }
.drawer-sidebar { width: auto; border: none; border-radius: 0; position: static; max-height: none; }

@media (max-width: 768px) {
    .deals-search-page { padding: 12px; }
    .deals-grid { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
    .page-header h1 { font-size: 20px; }
    .desktop-sidebar { display: none; }
    .mobile-filter-btn { display: inline-flex; }
    .drawer-overlay { display: block; }
}
</style>
